import type { JSONContent } from '@tiptap/core';
import {
  extractTextNodes,
  mapTranslatableStrings,
} from '@duke-hq/shared-constants/translationSegmentExtract';
import type { SegmentWithOverride } from './useCanvasTranslation';

// Mirrors the worker's walk and precedence, so the canvas shows what serving produces.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// A stale override is parked: the served value is the machine translation, not the override.
export function servedValueFor(row: SegmentWithOverride): string {
  if (row.override !== undefined && !row.override.isStale) {
    const value = row.override.overrideValue?.trim() ?? '';
    if (value !== '') {
      return value;
    }
  }
  const machine = row.segment.currentMachineValue ?? '';
  if (machine.trim() !== '') {
    return machine;
  }
  return row.segment.sourceText;
}

export type TranslatedNode = {
  tiptap: JSONContent | undefined;
  options: { id: string; title: string }[] | undefined;
};

function toJsonContent(value: unknown): JSONContent | undefined {
  return isRecord(value) ? value : undefined;
}

function toOptions(value: unknown): { id: string; title: string }[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const options: { id: string; title: string }[] = [];
  for (const option of value) {
    if (
      isRecord(option) &&
      typeof option['id'] === 'string' &&
      typeof option['title'] === 'string'
    ) {
      options.push({ id: option['id'], title: option['title'] });
    }
  }
  return options;
}

export function translateNodeContent(
  node: { id: string; data?: { tiptap?: unknown; options?: unknown } },
  rowsForNode: SegmentWithOverride[],
): TranslatedNode | null {
  const data = node.data ?? {};
  // The single-node wrapper must match the shape the capture walk saw, or the cursor drifts.
  const nodeContent = { nodes: [{ id: node.id, data }] };
  const { segments } = extractTextNodes(nodeContent);

  // Edited-since-capture content falls back to English (null) rather than splicing
  // into wrong slots; the per-segment check catches same-count edits the length can't.
  if (segments.length !== rowsForNode.length) {
    return null;
  }
  for (let i = 0; i < segments.length; i += 1) {
    if (segments[i] !== rowsForNode[i]?.segment.sourceText) {
      return null;
    }
  }

  let index = 0;
  const next = () => {
    const row = rowsForNode[index];
    index += 1;
    return row === undefined ? '' : servedValueFor(row);
  };

  // Reassemble through the SAME conversation wrapper the capture walk used, so
  // emit order (node body, then reply titles) and option handling stay aligned.
  const overlaid = mapTranslatableStrings(nodeContent, next);
  const overlaidNode =
    isRecord(overlaid) && Array.isArray(overlaid['nodes']) ? overlaid['nodes'][0] : undefined;
  const overlaidData =
    isRecord(overlaidNode) && isRecord(overlaidNode['data']) ? overlaidNode['data'] : {};

  return {
    tiptap: toJsonContent(overlaidData['tiptap']),
    options: toOptions(overlaidData['options']),
  };
}
